import { styled, Typography, useTheme } from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import { ChkLottieAnimation } from '../ChkLottieAnimation/ChkLottieAnimation';

import {
  STATUS_SCREEN_TITLE_ID,
  STATUS_SCREEN_TEXT_ID,
} from './ChkStatusScreen.constants';
import type {
  ChkStatusScreenHandlers,
  ChkStatusScreenProps,
} from './ChkStatusScreen.types';

export const ChkStatusScreen = forwardRef<
  ChkStatusScreenHandlers,
  ChkStatusScreenProps
>(
  (
    {
      title,
      text,
      animation,
      children,
      loop = true,
      disableTitleAutoFocus,
      ...rest
    },
    ref
  ) => {
    const { tokens, typography } = useTheme();
    const titleRef = useRef<HTMLHeadingElement>(null);

    useImperativeHandle(ref, () => ({
      focusTitle: () => {
        titleRef.current?.focus();
      },
    }));

    useEffect(() => {
      if (!disableTitleAutoFocus) {
        titleRef.current?.focus();
      }
    }, [disableTitleAutoFocus]);

    return (
      <StyledStatusScreen {...rest}>
        <StyledAnimationWrapper aria-hidden="true">
          <ChkLottieAnimation loop={loop} src={animation} />
        </StyledAnimationWrapper>
        {(title || text) && (
          <StyledTextWrapper>
            <Typography
              ref={titleRef}
              variant="h2"
              component="h1"
              color={tokens.statusValueHeader}
              fontWeight={typography.fontWeightMedium}
              data-testid={STATUS_SCREEN_TITLE_ID}
              sx={{ outline: 'none' }}
              tabIndex={-1}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              color={tokens.statusValueBody}
              data-testid={STATUS_SCREEN_TEXT_ID}
              sx={{ outline: 'none' }}
            >
              {text}
            </Typography>
          </StyledTextWrapper>
        )}
        {children}
      </StyledStatusScreen>
    );
  }
);

const StyledStatusScreen = styled('main')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  flex: '1 1 auto',

  [theme.breakpoints.up('xs')]: {
    minHeight: '49.2rem',
  },
}));

const StyledTextWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  textAlign: 'center',
}));

const StyledAnimationWrapper = styled('div')({
  maxWidth: '15rem',
});
